<template>
  <div class="login-form">
    <div class="login-form__header">
      <h1 class="login-form__title">{{ $t('login.sign-in-title') }}</h1>
      <p class="login-form__subtitle">{{ $t('login.sign-in-subtitle') }}</p>
    </div>

    <div
      v-if="hasEmailVerifyError"
      ref="errorBox"
      tabindex="-1"
      class="login-form__error"
      role="alert"
      aria-live="assertive"
    >
      {{ emailVerifyError }}
      <button type="button" class="login-form__resend-email" @click="sendEmail">
        {{ $t('login.resend-email') }}
      </button>
    </div>
    <div
      v-else-if="hasLoginError"
      ref="errorBox"
      tabindex="-1"
      class="login-form__error"
      role="alert"
      aria-live="assertive"
    >
      {{ loginError }}
    </div>

    <form @submit.prevent="onSubmit" novalidate>
      <div class="login-form__field">
        <FormInput
          :error="emailError"
          :label="$t('login.id')"
          has-gray-background
          name="email"
          type="email"
          ref="email"
          v-model="email"
          v-validate="'required|email'"
          autocomplete="email"
        />
      </div>

      <div class="login-form__field">
        <FormInput
          :error="passwordError"
          :label="$t('login.password')"
          :type="passwordType"
          has-gray-background
          name="password"
          v-model="password"
          v-validate="'required'"
          autocomplete="current-password"
          @keyup="checkCapsLock"
          @keydown="checkCapsLock"
        >
          <template #top-right>
            <button @click.prevent="togglePassword" class="login-form__toggle-password" type="button">
              <BaseIcon name="eye" v-if="passwordHidden" aria-hidden="true" />
              <BaseIcon name="eye-off" v-else aria-hidden="true" />
              {{ passwordHidden ? $t('reset-password.see') : $t('reset-password.hide') }}
            </button>
          </template>
        </FormInput>
        <p v-if="capsLockOn" class="login-form__caps-warning" role="status">
          {{ $t('login.caps-lock') }}
        </p>
        <div class="login-form__forgot-password">
          <BaseLink :to="{ name: 'forgot-password' }">
            {{ $t('login.forgot-pwd') }}
          </BaseLink>
        </div>
      </div>

      <div class="login-form__remember-me">
        <FormCheckbox :label="$t('login.remember-me')" :value="false" v-model="rememberMe" />
      </div>

      <div class="login-form__submit">
        <BaseButton
          is-large
          is-full-width
          :disabled="isFormInvalid || pending"
          :is-loading="pending"
          :is-outlined="isFormInvalid"
          ref="loginSubmit"
          type="submit"
        >
          {{ $t('login.log-in') }}
        </BaseButton>
      </div>
    </form>

    <div class="login-form__footer">
      <span class="login-form__footer-text">{{ $t('login.no-account') }}</span>
      <BaseLink :href="websiteLink" targetBlank>
        {{ $t('login.create-account') }}
      </BaseLink>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import BaseIcon from '@/components/base/BaseIcon';
import BaseLink from '@/components/base/BaseLink';
import BaseButton from '@/components/base/BaseButton.vue';
import { Beacon } from '@/plugins/beacon';
import { Config } from '@/config';
import FormInput from '@/components/form/FormInput.vue';
import FormCheckbox from '@/components/form/FormCheckbox';
import { LOCALE } from '@/util/i18n/locale';
import { localize } from '@/util/i18n/localize';

export default {
  components: {
    BaseIcon,
    BaseLink,
    BaseButton,
    FormInput,
    FormCheckbox,
  },
  data: function () {
    return {
      passwordHidden: true,
      rememberMe: true,
      email: '',
      password: '',
      logging: false,
      capsLockOn: false,
    };
  },
  inject: ['$validator'],
  computed: {
    ...mapGetters('auth', ['pending', 'error']),
    ...mapGetters('tenant', ['tenant']),
    passwordType() {
      return this.passwordHidden ? 'password' : 'text';
    },
    isFormInvalid() {
      return !!!this.email || !!!this.password || !!this.emailError || !!this.passwordError;
    },
    emailError() {
      const error = this.errors.items.find((x) => x.field === 'email');

      return error ? error.msg : undefined;
    },
    passwordError() {
      const error = this.errors.items.find((x) => x.field === 'password');

      return error ? error.msg : undefined;
    },
    emailVerifyError() {
      return this.error && this.error.type === 'EMAIL_NOT_VERIFIED'
        ? this.$t('login.email-not-verified-error')
        : undefined;
    },
    loginError() {
      return this.error && this.error.type !== 'EMAIL_NOT_VERIFIED' ? this.$t('login.unknown-error') : undefined;
    },
    hasEmailVerifyError() {
      return this.emailVerifyError !== undefined;
    },
    hasLoginError() {
      return this.loginError !== undefined;
    },
    websiteLink() {
      return this.$i18n.locale === LOCALE.EN_CA ? Config.website.en : Config.website.fr;
    },
  },
  mounted() {
    const refInput = this.$refs.email.$el.querySelector('input');
    if (refInput) {
      refInput.focus();
    }
  },
  methods: {
    ...mapActions('auth', ['login']),
    ...mapActions('sendEmailConfirmation', ['resendEmailFromLogin']),
    checkCapsLock(event) {
      if (typeof event.getModifierState === 'function') {
        this.capsLockOn = event.getModifierState('CapsLock');
      }
    },
    onSubmit() {
      this.email = this.email.trim();
      this.errors.remove('email-not-verified');
      this.$validator.validate().then(() => {
        this.onLogin();
      });
    },
    async onLogin() {
      try {
        const { user } = await this.login({
          rememberMe: this.rememberMe,
          email: this.email.trim().toLowerCase(),
          password: this.password,
        });
        localize(user.locale);
        Beacon.initialize();
        Beacon.prefill(user);
        await this.$router.push(this.$router.history.current.query.redirect || '/');
        this.$track.login();
      } catch (error) {
        if (error.type === 'EMAIL_NOT_VERIFIED') {
          this.errors.add({
            field: 'email-not-verified',
            msg: this.$t('login.email-not-verified-error'),
            email: this.email,
          });
        }
        this.$nextTick(() => {
          const errorBox = this.$refs.errorBox;
          if (errorBox) {
            errorBox.focus();
            errorBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
      }
    },
    togglePassword() {
      this.passwordHidden = !this.passwordHidden;
    },
    sendEmail() {
      const error = this.errors.items.find((x) => x.field === 'email-not-verified');

      if (!error) {
        return;
      }

      this.resendEmailFromLogin(error.email)
        .then(() => {
          this.$toast.success(this.$t('login.resend-email-success'));
        })
        .catch((error) => {
          if (error.type === 'TOKEN_NOT_USED') {
            this.$toast.warning(this.$t('login.resend-email-error'));
          }
        });
    },
  },
};
</script>

<style lang="scss">
.login-form {
  display: flex;
  flex-direction: column;
  gap: $space-lg;

  &__header {
    display: flex;
    flex-direction: column;
    gap: $space-xs;
  }

  &__title {
    color: $color-normal;
    font-family: $font-family-heading;
    @include text-style('heading-xl');
    margin: 0;
  }

  &__subtitle {
    color: $color-text-body;
    font-family: $font-family-body;
    @include text-style('body-md');
    margin: 0;
  }

  &__error {
    background-color: $color-semantic-error-light;
    border-radius: $radius-md;
    color: $color-text-error;
    @include text-style('body-md');
    padding: $space-md;
  }

  &__resend-email {
    appearance: none;
    background: none;
    border: 0;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    display: inline;
    margin-top: $space-xs;
    text-decoration: underline;
  }

  &__field {
    display: flex;
    flex-direction: column;
    gap: $space-xs;
    margin-bottom: $space-md;
  }

  &__forgot-password {
    display: flex;
    justify-content: flex-end;
    @include text-style('body-sm');
  }

  &__caps-warning {
    color: $color-semantic-warning;
    @include text-style('body-sm');
    margin: 0;
  }

  &__remember-me {
    color: $color-text-body;
  }

  &__submit {
    margin-top: $space-sm;
  }

  &__toggle-password {
    align-items: center;
    background: none;
    border: none;
    color: $color-text-body;
    cursor: pointer;
    display: flex;
    @include text-style('body-sm');
    gap: $space-xs;
    outline: none;

    &:hover,
    &:active,
    &:focus {
      color: $color-text-link;
    }

    .feather {
      height: map-get(map-get($aloha-sizes, 'md'), 'icon-size');
      width: map-get(map-get($aloha-sizes, 'md'), 'icon-size');
    }
  }

  &__footer {
    align-items: center;
    border-top: $grey-border;
    display: flex;
    gap: $space-xs;
    justify-content: center;
    padding-top: $space-lg;
  }

  &__footer-text {
    color: $color-text-body;
    @include text-style('body-md');
  }
}
</style>
